import { useMemo } from 'react';
import { useHref, useLocation } from 'react-router-dom';
import { useErrorLogService } from 'ErrorLogging';
import { modifyHref, modifyTarget } from './linkHelpers';
import { type LinkTarget } from './Navigation.types';

const useSafeLocation = () => {
    try {
        return useLocation();
    } catch {
        // no-op
    }

    return null;
};

const useSafeHref = (href: string | null | undefined) => {
    try {
        return useHref(href || '');
    } catch {
        // no-op
    }

    return href;
};

const useModifyHref = (href: string | null | undefined) => {
    const location = useSafeLocation();
    const ErrorLogService = useErrorLogService();

    // the react-router-dom <Link> component that pixelmatters previously used treats "." as the current page,
    // so we're re-implementing that behavior here
    if (href === '.') {
        href = location?.pathname || null;

        if (href === null) {
            ErrorLogService.notify('Cannot use href="." outside of react router', null);
        }
    }
    href = useMemo(() => (href ? modifyHref(href) : href), [href]);

    const reactRouterSafeHref = useSafeHref(href);

    if (href && href.startsWith('/')) {
        // only assign if href is defined because the react router hook will return the current route if href is undefined
        href = reactRouterSafeHref;
    }

    return href;
};

// This hook takes an href and target and returns the modified versions of them.
// If we ever succeed in removing AnchorLink and completely unifying our link components then
// this logic can move to the ClickableText component (which might have been renamed to Link by then)
export function useLinkParams({ href, target }: { href?: string | null; target?: LinkTarget | null }) {
    href = useModifyHref(href);
    target = useMemo(() => modifyTarget(href, target), [href, target]);

    return { href, target };
}
